// A message that must outlive the page it was asked from (28 Oct, the sign-in build, item 4 review): Change password
// signs the person out straight after the reset email is sent, and the sign-out resets the store, so the page that asked
// (the profile page, the waiting page) is replaced while the message is still to be read. The message is therefore shown
// by components/Account/AccountNotice.tsx, mounted in BaseLayout above every guard, and the sign-out waits for it to be
// closed before it leaves the page. Fixed text from this app only.
//
// NEVER STUCK. BaseLayout is mounted by two layouts (the main one and checkout's). If the notice that is showing the
// message unmounts (the store reset sends checkout's waiting page to /sign-in under the main layout), the message stays
// pending here and the next notice to mount shows it; if none mounts within a second, it is released, so the sign-out
// still leaves the page (the person is signed out either way).

export type AccountNoticeContent = { title: string; text: string };

type Listener = (content: AccountNoticeContent) => void;

const RELEASE_AFTER_MS = 1000;

let listener: Listener | null = null;
let pending: { content: AccountNoticeContent; resolve: () => void } | null = null;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;

const release = () => {
  if (releaseTimer) clearTimeout(releaseTimer);
  releaseTimer = null;
  const done = pending?.resolve;
  pending = null;
  done?.();
};

/** Show the message; resolves when the person closes it. With nothing mounted to show it (tests), resolves at once. */
export const showAccountNotice = (content: AccountNoticeContent): Promise<void> =>
  new Promise((resolve) => {
    if (!listener) {
      resolve();
      return;
    }
    if (pending) release();
    pending = { content, resolve };
    listener(content);
  });

/** The mounted notice's close: the message has been read. */
export const accountNoticeClosed = (): void => release();

/** Mount (a listener) or unmount (null). A mount shows a message still pending; an unmount keeps it for a second. */
export const setAccountNoticeListener = (next: Listener | null): void => {
  listener = next;
  if (next) {
    if (releaseTimer) clearTimeout(releaseTimer);
    releaseTimer = null;
    if (pending) next(pending.content);
  } else if (pending && !releaseTimer) {
    releaseTimer = setTimeout(release, RELEASE_AFTER_MS);
  }
};

/** Tests only: forget a message left pending by an earlier test. */
export const __resetAccountNoticeForTests = (): void => release();
